import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Building2, AlertTriangle, Clock, TrendingUp, Users, MailWarning, MessageSquareOff,
  MessageSquareWarning, ClipboardList,
} from 'lucide-react';
import { useApi } from '@/lib/useApi';
import { date, pluralise } from '@/lib/format';
import {
  Badge, Card, CardHeader, EmptyState, LoadingBlock, PageHeader, SearchInput, Select,
  Table, TableWrap, Td, Th, cn,
} from '@/components/ui';
import type { AdminOrgSummary, AdminOverview } from '@shared/types';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral' | 'info'> = {
  active: 'ok',
  trialing: 'info',
  lapsed: 'warn',
  cancelled: 'danger',
};

export default function Organisations() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const { data: overview } = useApi<AdminOverview>('/admin/overview');
  const { data: orgs, loading } = useApi<AdminOrgSummary[]>('/admin/organisations',
    { q: search || undefined, status: status || undefined });

  return (
    <>
      <PageHeader
        title="Organisations"
        subtitle={overview
          ? `${overview.organisations} accounts · ${overview.totals.properties} properties · ${overview.totals.tenants} tenants`
          : 'Every account on this deployment'}
      />

      {/*
        What needs attention, before what exists. A list of accounts is always
        available below; the two things that cost money if nobody looks are a
        trial about to end and an account that has already lapsed.
      */}
      {overview && (overview.trials_ending_soon.length > 0 || overview.lapsed.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {overview.trials_ending_soon.length > 0 && (
            <AttentionCard
              icon={<Clock className="h-4 w-4" />}
              tone="info"
              title={`${pluralise(overview.trials_ending_soon.length, 'trial')} ending within a week`}
              rows={overview.trials_ending_soon.map((t) => ({
                id: t.id,
                name: t.name,
                note: t.days_left <= 0 ? 'ends today' : `${pluralise(t.days_left, 'day')} left`,
              }))}
              onPick={(id) => navigate(`/admin/organisations/${id}`)}
            />
          )}
          {overview.lapsed.length > 0 && (
            <AttentionCard
              icon={<AlertTriangle className="h-4 w-4" />}
              tone="warn"
              title={`${pluralise(overview.lapsed.length, 'account')} lapsed`}
              rows={overview.lapsed.map((o) => ({
                id: o.id,
                name: o.name,
                note: o.contact_email ?? 'no contact address',
              }))}
              onPick={(id) => navigate(`/admin/organisations/${id}`)}
            />
          )}
        </div>
      )}

      {overview && (!overview.email_configured || !overview.sms_configured) && (
        <div className="flex flex-wrap items-center gap-3 mb-6 text-sm">
          {!overview.email_configured && (
            <span className="flex items-center gap-2 text-warn bg-warn/10 border
                             border-warn/25 rounded-xl px-3 py-2">
              <MailWarning className="h-4 w-4 shrink-0" />
              Email is not configured — messages will be written to the execution log
            </span>
          )}
          {!overview.sms_configured && (
            <span className="flex items-center gap-2 text-warn bg-warn/10 border
                             border-warn/25 rounded-xl px-3 py-2">
              <MessageSquareOff className="h-4 w-4 shrink-0" />
              Twilio is not configured — texts are recorded as skipped
            </span>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name or contact address…"
          className="flex-1"
        />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="sm:w-48">
          <option value="">Every status</option>
          <option value="trialing">Trialing</option>
          <option value="active">Active</option>
          <option value="lapsed">Lapsed</option>
          <option value="cancelled">Cancelled</option>
        </Select>
      </div>

      <Card>
        {loading ? (
          <LoadingBlock rows={6} />
        ) : !orgs || orgs.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-6 w-6" />}
            title="Nothing matches"
            message={search || status
              ? 'No account matches that search.'
              : 'No organisation has signed up yet.'}
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Organisation</Th>
                  <Th>Status</Th>
                  <Th>Plan</Th>
                  <Th align="right">Properties</Th>
                  <Th align="right">Tenants</Th>
                  <Th>Signed up</Th>
                </tr>
              </thead>
              <tbody>
                {orgs.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() => navigate(`/admin/organisations/${org.id}`)}
                    className="cursor-pointer hover:bg-raised/60 transition-colors"
                  >
                    <Td>
                      <div className="font-medium text-ink flex items-center gap-2">
                        {org.name}
                        {org.is_demo && <Badge tone="neutral">demo</Badge>}
                      </div>
                      <div className="text-xs text-faint mt-0.5">
                        {org.contact_email ?? 'no contact address'}
                      </div>
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[org.subscription_status] ?? 'neutral'}>
                        {org.subscription_status}
                      </Badge>
                      {org.subscription_status === 'trialing' && org.trial_days_left !== null && (
                        <div className="text-xs text-faint mt-1">
                          {org.trial_days_left <= 0
                            ? 'ends today'
                            : `${pluralise(org.trial_days_left, 'day')} left`}
                        </div>
                      )}
                    </Td>
                    <Td>
                      <span className="capitalize">{org.plan}</span>
                      <div className="text-xs text-faint mt-0.5">
                        limit {org.property_limit || '—'}
                      </div>
                    </Td>
                    {/*
                      Over the limit is worth seeing at a glance: it is either a
                      customer to move up a plan, or a limit somebody forgot to
                      raise after agreeing to.
                    */}
                    <Td align="right">
                      <span className={cn(
                        org.property_limit > 0 && org.counts.properties > org.property_limit
                          && 'text-warn font-medium',
                      )}>
                        {org.counts.properties}
                      </span>
                    </Td>
                    <Td align="right">{org.counts.tenants}</Td>
                    <Td>{date(org.created_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
          <MiniStat icon={<TrendingUp className="h-4 w-4" />} label="New this month"
            value={overview.signups_last_30_days} />
          <MiniStat icon={<Building2 className="h-4 w-4" />} label="Active"
            value={overview.counts.active ?? 0} />
          <MiniStat icon={<Clock className="h-4 w-4" />} label="On trial"
            value={overview.counts.trialing ?? 0} />
          <MiniStat icon={<Users className="h-4 w-4" />} label="Staff accounts"
            value={overview.totals.users} />
          {/*
            The two that are work rather than a number to look at, so they are
            links: an unread report and a customer waiting on a phone number
            are both somebody's next job.
          */}
          <Link to="/admin/inbox">
            <MiniStat icon={<MessageSquareWarning className="h-4 w-4" />} label="Unread feedback"
              value={overview.feedback_new} />
          </Link>
          <Link to="/admin/inbox">
            <MiniStat icon={<ClipboardList className="h-4 w-4" />} label="Open jobs"
              value={overview.tasks_open} />
          </Link>
        </div>
      )}
    </>
  );
}

function AttentionCard({ icon, tone, title, rows, onPick }: {
  icon: React.ReactNode;
  tone: 'info' | 'warn';
  title: string;
  rows: { id: string; name: string; note: string }[];
  onPick: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader
        title={(
          <span className={cn('flex items-center gap-2',
            tone === 'warn' ? 'text-warn' : 'text-brand')}>
            {icon} {title}
          </span>
        )}
      />
      <div className="px-5 sm:px-6 pb-5 pt-1 space-y-1">
        {rows.slice(0, 6).map((row) => (
          <button
            key={row.id}
            onClick={() => onPick(row.id)}
            className="w-full flex items-center justify-between gap-3 text-left text-sm
                       rounded-lg px-2 py-1.5 -mx-2 hover:bg-raised transition-colors"
          >
            <span className="text-ink truncate">{row.name}</span>
            <span className="text-faint shrink-0">{row.note}</span>
          </button>
        ))}
        {rows.length > 6 && (
          <p className="text-xs text-faint pt-1">and {rows.length - 6} more</p>
        )}
      </div>
    </Card>
  );
}

function MiniStat({ icon, label, value }: {
  icon: React.ReactNode; label: string; value: number;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-faint text-sm">{icon}{label}</div>
      <p className="text-2xl font-semibold text-ink mt-1.5 tabular-nums">{value}</p>
    </Card>
  );
}
